import { ChevronUp, ChevronDown, GripVertical } from "lucide-react";

/**
 * Compact reorder controls (up + down chevrons) with a tiny grip glyph.
 * Reusable across any list that supports moving items up/down.
 *
 * Props:
 *   - onUp / onDown : handlers
 *   - canUp / canDown : booleans to disable the ends of the list
 *   - testidPrefix : namespace for data-testids
 *   - orientation : "vertical" (default) | "horizontal"
 */
export default function ReorderControls({
  onUp,
  onDown,
  canUp = true,
  canDown = true,
  testidPrefix = "reorder",
  orientation = "vertical",
}) {
  const wrapCls =
    orientation === "vertical"
      ? "inline-flex flex-col items-stretch"
      : "inline-flex items-stretch";
  const btnCls = (edge) =>
    "flex items-center justify-center px-1.5 py-1 hover:bg-white/5 " +
    "disabled:opacity-25 disabled:cursor-not-allowed transition-colors " +
    (orientation === "vertical" ? (edge === "start" ? "border-b" : "") : (edge === "start" ? "border-r" : "")) +
    " border-cmdBorder";
  return (
    <div
      className={`${wrapCls} border border-cmdBorder rounded-sm relative`}
      data-testid={`${testidPrefix}-wrap`}
      title="Reorder"
    >
      <button
        type="button"
        onClick={onUp}
        disabled={!canUp}
        className={btnCls("start")}
        aria-label="Move up"
        data-testid={`${testidPrefix}-up`}
      >
        <ChevronUp className="w-3 h-3" strokeWidth={2.2} />
      </button>
      <button
        type="button"
        onClick={onDown}
        disabled={!canDown}
        className={btnCls("end")}
        aria-label="Move down"
        data-testid={`${testidPrefix}-down`}
      >
        <ChevronDown className="w-3 h-3" strokeWidth={2.2} />
      </button>
    </div>
  );
}

/**
 * Alternate horizontal-bar style with an explicit grip glyph for lists where a
 * clear "drag handle" affordance matters, e.g., dense inline rows.
 */
export function ReorderBar({ onUp, onDown, canUp, canDown, testidPrefix = "reorder" }) {
  return (
    <div className="inline-flex items-center gap-0 border border-cmdBorder rounded-sm overflow-hidden" data-testid={`${testidPrefix}-bar`}>
      <button type="button" onClick={onUp} disabled={!canUp} className="px-1.5 py-1 hover:bg-white/5 disabled:opacity-25 disabled:cursor-not-allowed" aria-label="Move up" data-testid={`${testidPrefix}-up`}>
        <ChevronUp className="w-3 h-3" strokeWidth={2.2} />
      </button>
      <span className="px-0.5 text-zinc-500 pointer-events-none"><GripVertical className="w-3 h-3" /></span>
      <button type="button" onClick={onDown} disabled={!canDown} className="px-1.5 py-1 hover:bg-white/5 disabled:opacity-25 disabled:cursor-not-allowed" aria-label="Move down" data-testid={`${testidPrefix}-down`}>
        <ChevronDown className="w-3 h-3" strokeWidth={2.2} />
      </button>
    </div>
  );
}
